/**
 * MeasurementsPrint - Print functionality component
 * Dropdown menu with print settings and column selection
 */

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Printer } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { PrintSettings } from "../types";

interface MeasurementsPrintProps {
  printSettings: PrintSettings;
  onSettingsChange: (settings: PrintSettings) => void;
  onToggleAllColumns: (checked: boolean) => void;
  areAllColumnsSelected: boolean;
  onPrint: () => void;
}

export function MeasurementsPrint({
  printSettings,
  onSettingsChange,
  onToggleAllColumns,
  areAllColumnsSelected,
  onPrint,
}: MeasurementsPrintProps) {
  const { t } = useTranslation();

  const handleColumnToggle = (column: keyof PrintSettings["columns"]) => {
    onSettingsChange({
      ...printSettings,
      columns: {
        ...printSettings.columns,
        [column]: !printSettings.columns[column],
      },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Printer className="h-4 w-4 mr-2" />
          {t("measurements:print.button")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          {t("measurements:print.settings")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Include Summary */}
        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="includeSummary"
            checked={printSettings.includeSummary}
            onCheckedChange={checked =>
              onSettingsChange({
                ...printSettings,
                includeSummary: checked as boolean,
              })
            }
          />
          <label
            htmlFor="includeSummary"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:print.includeSummary")}
          </label>
        </div>

        {/* Include Charts */}
        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="includeCharts"
            checked={printSettings.includeCharts}
            onCheckedChange={checked =>
              onSettingsChange({
                ...printSettings,
                includeCharts: checked as boolean,
              })
            }
          />
          <label
            htmlFor="includeCharts"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:print.includeCharts")}
          </label>
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("measurements:print.columns")}</DropdownMenuLabel>

        {/* Select All Columns */}
        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="selectAll"
            checked={areAllColumnsSelected}
            onCheckedChange={onToggleAllColumns}
          />
          <label
            htmlFor="selectAll"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:print.selectAll")}
          </label>
        </div>

        <DropdownMenuSeparator />

        {/* Individual Column Checkboxes */}
        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="col-date"
            checked={printSettings.columns.date}
            onCheckedChange={() => handleColumnToggle("date")}
          />
          <label
            htmlFor="col-date"
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:table.date")}
          </label>
        </div>

        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="col-weight"
            checked={printSettings.columns.weight}
            onCheckedChange={() => handleColumnToggle("weight")}
          />
          <label
            htmlFor="col-weight"
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:table.weight")}
          </label>
        </div>

        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="col-bodyFat"
            checked={printSettings.columns.bodyFat}
            onCheckedChange={() => handleColumnToggle("bodyFat")}
          />
          <label
            htmlFor="col-bodyFat"
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:table.bodyFat")}
          </label>
        </div>

        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="col-muscleMass"
            checked={printSettings.columns.muscleMass}
            onCheckedChange={() => handleColumnToggle("muscleMass")}
          />
          <label
            htmlFor="col-muscleMass"
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:table.muscle")}
          </label>
        </div>

        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="col-visceralFat"
            checked={printSettings.columns.visceralFat}
            onCheckedChange={() => handleColumnToggle("visceralFat")}
          />
          <label
            htmlFor="col-visceralFat"
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:table.visceralFat")}
          </label>
        </div>

        <div className="flex items-center space-x-2 px-2 py-2">
          <Checkbox
            id="col-bmr"
            checked={printSettings.columns.bmr}
            onCheckedChange={() => handleColumnToggle("bmr")}
          />
          <label
            htmlFor="col-bmr"
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t("measurements:table.bmr")}
          </label>
        </div>

        <DropdownMenuSeparator />

        {/* Print Button */}
        <DropdownMenuItem onClick={onPrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t("measurements:print.printCurrentClient")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
